import { metricDisplayName } from './systemMetrics.js';

const operators = {
  '>': 'بیشتر از',
  '<': 'کمتر از',
  '>=': 'بیشتر یا مساوی',
  '<=': 'کمتر یا مساوی',
  '==': 'برابر با',
  '!=': 'متفاوت از',
  between: 'بین',
  not_between: 'خارج از بازه',
};
const severities = { info: 'اطلاعاتی', warning: 'هشدار', critical: 'بحرانی' };
const present = (v) =>
  v !== '' && v !== null && v !== undefined && Number.isFinite(Number(v));
const number = (v) =>
  Number(v).toLocaleString('fa-IR', { maximumFractionDigits: 6 });
const metricName = (metric, label) => label || metricDisplayName(metric);

export function readableDuration(value) {
  if (!present(value) || Number(value) <= 0) return '';
  const seconds = Number(value);
  if (seconds % 86400 === 0) return `${number(seconds / 86400)} روز`;
  if (seconds % 3600 === 0) return `${number(seconds / 3600)} ساعت`;
  if (seconds % 60 === 0) return `${number(seconds / 60)} دقیقه`;
  return `${number(seconds)} ثانیه`;
}

export function describeRule(
  values,
  { targetName, metricLabel, channelName } = {},
) {
  const condition = values.condition_expression || values;
  const targetId =
    values.target_type === 'tag' ? values.target_tag_id : values.target_eui;
  const target = targetName || values.target_name || targetId;
  const incomplete = {
    text: 'هدف، نوع قانون و شرط را تکمیل کنید تا خلاصهٔ رفتار آن نمایش داده شود.',
    notes: [],
  };
  if (!target || !values.rule_type || !severities[values.severity])
    return incomplete;
  const scope =
    values.target_type === 'tag'
      ? `هر عضو تگ «${target}» به‌صورت مستقل`
      : `${values.target_type === 'gateway' ? 'هاب' : 'دستگاه'} «${target}»`;
  const notes = [];
  let when;
  if (values.rule_type === 'lifecycle') {
    // Same cadence-derived duration as the payload builder; reject invalid drafts.
    const cadence = condition.expected_interval_seconds;
    const missed = condition.missed_intervals;
    const duration =
      cadence !== undefined
        ? Number(cadence) * Number(missed)
        : Number(condition.duration_seconds);
    if (
      !Number.isInteger(duration) ||
      duration <= 0 ||
      duration > 2592000 ||
      (cadence !== undefined &&
        (!Number.isInteger(Number(cadence)) ||
          Number(cadence) < 1 ||
          !Number.isInteger(Number(missed)) ||
          Number(missed) < 1 ||
          Number(missed) > 100))
    )
      return incomplete;
    const metric = condition.metric_name
      ? `دادهٔ «${metricName(condition.metric_name, metricLabel)}»`
      : 'هیچ پیام داده‌ای';
    when = `اگر از ${scope} به مدت ${readableDuration(duration)} ${metric} دریافت نشود`;
    notes.push(
      'این قانون نرسیدن داده را تشخیص می‌دهد؛ لزوماً به معنی خاموش بودن دستگاه نیست.',
    );
    notes.push(
      'پس از فعال‌سازی، تغییر تنظیمات یا عضویت جدید، یک مهلت کامل شروع می‌شود. هر دورهٔ بی‌داده یک هشدار دارد و با دریافت دادهٔ معتبر، خودکار رفع می‌شود.',
    );
  } else if (['threshold', 'timewindow'].includes(values.rule_type)) {
    if (!condition.metric_name || !operators[condition.operator])
      return incomplete;
    const isRange = ['between', 'not_between'].includes(condition.operator);
    const first =
      typeof condition.value === 'object' && condition.value !== null
        ? (condition.value.min ?? condition.value[0])
        : condition.value;
    const second =
      condition.value2 ?? condition.value?.max ?? condition.value?.[1];
    if (
      !present(first) ||
      (isRange && (!present(second) || Number(first) > Number(second)))
    )
      return incomplete;
    const range = isRange
      ? `${number(first)} تا ${number(second)}`
      : number(first);
    when = `اگر مقدار «${metricName(condition.metric_name, metricLabel)}» برای ${scope} ${operators[condition.operator]} ${range} باشد`;
    if (values.rule_type === 'timewindow') {
      const duration = readableDuration(condition.duration_seconds);
      if (!duration) return incomplete;
      when += ` و این وضعیت در داده‌های دریافتی دست‌کم ${duration} ادامه داشته باشد`;
      notes.push(
        'پس از گذشت این مدت، دریافت نمونهٔ بعدیِ مطابق شرط هشدار را فعال می‌کند؛ این قانون جایگزین پایش عدم دریافت داده نیست.',
      );
    }
  } else return { text: 'خلاصهٔ این نوع قانون هنوز در دسترس نیست.', notes: [] };
  const text = `${when}، آنگاه هشدار با اهمیت «${severities[values.severity]}» در پنل ثبت می‌شود.`;
  if (channelName || values.channel_id)
    notes.push(
      `اطلاع‌رسانی از کانال «${channelName || values.channel_id}» مطابق تنظیمات آن انجام می‌شود.`,
    );
  else
    notes.push(
      'بدون کانال اطلاع‌رسانی، هشدار فقط داخل پنل ثبت می‌شود؛ برای ارسال پیام، کانال را انتخاب یا به قانون متصل کنید.',
    );
  if (values.is_active === false)
    notes.unshift(
      'این قانون غیرفعال است؛ رفتار زیر فقط پس از فعال‌سازی اجرا می‌شود.',
    );
  return { text, notes };
}
